import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';

const API = process.env.REACT_APP_BACKEND_URL;
const COLORS = {
  primary: '#2b3380',
  whatsapp: '#25D366',
  success: '#4caf50',
  warning: '#ff9800'
};

function WhatsAppInbox({ user, onLogout }) {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [replyText, setReplyText] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchConversations();
    // Poll for new messages every 5 seconds
    const interval = setInterval(fetchConversations, 5000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (selectedConversation) {
      fetchMessages(selectedConversation.phone_number);
    }
  }, [selectedConversation]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchConversations = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/whatsapp/messages?limit=100`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Group messages by phone number
      const grouped = {};
      response.data.messages.forEach(msg => {
        const phone = msg.from || msg.to;
        if (!grouped[phone]) {
          grouped[phone] = {
            phone_number: phone,
            last_message: msg.message_body || msg.message || 'Media message',
            last_message_time: msg.logged_at || msg.timestamp,
            unread: msg.type === 'received' && !msg.is_read,
            contact_name: phone
          };
        }
      });

      setConversations(Object.values(grouped));
    } catch (error) {
      console.error('Error fetching conversations:', error);
    }
  };

  const fetchMessages = async (phone) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/whatsapp/messages?limit=100`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Filter messages for this phone
      const filtered = response.data.messages.filter(
        msg => msg.from === phone || msg.to === phone
      );

      setMessages(filtered);
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!replyText.trim() || !selectedConversation) return;

    setSending(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/whatsapp/send-text`, {
        to: selectedConversation.phone_number,
        message: replyText
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setReplyText('');
      await fetchMessages(selectedConversation.phone_number);
      alert('✅ Message sent!');
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || 'Failed to send message. Note: Text messages only work within 24h window.'));
    } finally {
      setSending(false);
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;

    if (diff < 86400000) { // Less than 24 hours
      return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  };

  const isWithin24Hours = (timestamp) => {
    if (!timestamp) return false;
    const date = new Date(timestamp);
    const now = new Date();
    return (now - date) < 86400000; // 24 hours in milliseconds
  };

  const canReply = selectedConversation && messages.length > 0 && 
                   isWithin24Hours(messages[messages.length - 1]?.logged_at);

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="h-screen flex bg-gray-100">
        {/* Sidebar - Conversation List */}
        <div className="w-1/3 bg-white border-r flex flex-col" style={{ maxHeight: 'calc(100vh - 64px)' }}>
          {/* Header */}
          <div className="p-4 border-b" style={{ backgroundColor: COLORS.primary }}>
            <h2 className="text-xl font-bold text-white flex items-center">
              <span className="mr-2">💬</span> Team Inbox
            </h2>
            <p className="text-sm text-gray-200 mt-1">{conversations.length} conversations</p>
          </div>

          {/* Search */}
          <div className="p-3 border-b">
            <input
              type="text"
              placeholder="Search conversations..."
              className="w-full px-3 py-2 border rounded-lg text-sm"
            />
          </div>

          {/* Conversation List */}
          <div className="flex-1 overflow-y-auto">
            {conversations.map((conv, idx) => (
              <div
                key={idx}
                onClick={() => setSelectedConversation(conv)}
                className={`p-4 border-b cursor-pointer hover:bg-gray-50 ${
                  selectedConversation?.phone_number === conv.phone_number ? 'bg-blue-50' : ''
                }`}
              >
                <div className="flex items-start">
                  <div className="w-12 h-12 rounded-full bg-gray-300 flex items-center justify-center text-white font-bold mr-3">
                    {conv.contact_name[0]?.toUpperCase() || '?'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-start">
                      <h3 className="font-semibold text-sm truncate">{conv.contact_name}</h3>
                      <span className="text-xs text-gray-500">{formatTime(conv.last_message_time)}</span>
                    </div>
                    <p className="text-sm text-gray-600 truncate mt-1">{conv.last_message}</p>
                    <p className="text-xs text-gray-400 mt-1">{conv.phone_number}</p>
                  </div>
                  {conv.unread && (
                    <div className="ml-2 w-2 h-2 bg-green-500 rounded-full"></div>
                  )}
                </div>
              </div>
            ))}

            {conversations.length === 0 && (
              <div className="p-8 text-center text-gray-500">
                <p className="text-4xl mb-2">📭</p>
                <p>No conversations yet</p>
                <p className="text-sm mt-2">Messages will appear here when customers contact you</p>
              </div>
            )}
          </div>
        </div>

        {/* Main Chat Area */}
        <div className="flex-1 flex flex-col">
          {selectedConversation ? (
            <>
              {/* Chat Header */}
              <div className="bg-white border-b p-4 flex items-center justify-between">
                <div className="flex items-center">
                  <div className="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center text-white font-bold mr-3">
                    {selectedConversation.contact_name[0]?.toUpperCase() || '?'}
                  </div>
                  <div>
                    <h3 className="font-semibold">{selectedConversation.contact_name}</h3>
                    <p className="text-sm text-gray-500">{selectedConversation.phone_number}</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button className="px-3 py-1 text-sm bg-gray-100 rounded hover:bg-gray-200">
                    Assign
                  </button>
                  <button className="px-3 py-1 text-sm bg-gray-100 rounded hover:bg-gray-200">
                    Archive
                  </button>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 bg-gray-50">
                {loading ? (
                  <div className="text-center py-8 text-gray-500">Loading messages...</div>
                ) : (
                  <>
                    {messages.map((msg, idx) => {
                      const isSent = msg.type === 'sent_template' || msg.type === 'sent_text';
                      return (
                        <div
                          key={idx}
                          className={`mb-3 flex ${isSent ? 'justify-end' : 'justify-start'}`}
                        >
                          <div
                            className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                              isSent
                                ? 'bg-green-100 text-gray-800'
                                : 'bg-white text-gray-800 border'
                            }`}
                          >
                            <p className="text-sm whitespace-pre-wrap">
                              {msg.message_body || msg.message || msg.template || 'Message'}
                            </p>
                            <div className="flex items-center justify-end gap-1 mt-1">
                              <span className="text-xs text-gray-500">
                                {formatTime(msg.logged_at || msg.timestamp)}
                              </span>
                              {isSent && (
                                <span className="text-xs">
                                  {msg.status === 'sent' ? '✓' : 
                                   msg.status === 'delivered' ? '✓✓' : 
                                   msg.status === 'read' ? '✓✓' : '⏳'}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    <div ref={messagesEndRef} />
                  </>
                )}
              </div>

              {/* Reply Box */}
              <div className="bg-white border-t p-4">
                {canReply ? (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                      placeholder="Type a message..."
                      className="flex-1 px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      disabled={sending}
                    />
                    <button
                      onClick={handleSendMessage}
                      disabled={sending || !replyText.trim()}
                      className="px-6 py-2 text-white rounded-lg disabled:opacity-50"
                      style={{ backgroundColor: COLORS.whatsapp }}
                    >
                      {sending ? '⏳' : '📤'}
                    </button>
                  </div>
                ) : (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-center">
                    <p className="text-sm text-yellow-800">
                      ⚠️ 24-hour window expired. You can only reply within 24 hours of the last customer message.
                      <br />
                      <span className="text-xs">Use template messages to start new conversation.</span>
                    </p>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center bg-gray-50">
              <div className="text-center text-gray-400">
                <p className="text-6xl mb-4">💬</p>
                <p className="text-xl font-medium">Select a conversation</p>
                <p className="text-sm mt-2">Choose a conversation from the left to view messages</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}

export default WhatsAppInbox;
